import { Injectable, computed, inject, signal } from '@angular/core';
import { AdminInboxNotification } from '@core/models/admin/account/admin-inbox.model';
import { NotificationService } from './notification.service';

@Injectable({ providedIn: 'root' })
export class AdminInboxService {
  private notificationService = inject(NotificationService);

  private readonly _notifications = signal<AdminInboxNotification[]>([]);
  readonly notifications = this._notifications.asReadonly();

  readonly unreadCount = computed(
    () => this._notifications().filter((n) => n.read_at === null).length
  );

  // pagination state
  private _skip = 0;
  private _pageSize = 5;
  private _hasMore = signal(true);
  readonly hasMore = this._hasMore.asReadonly();

  private _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  // Refresh inbox and load the first page (default 5 items)
  refresh(pageSize = 5): void {
    this._pageSize = pageSize;
    this._skip = 0;
    this._hasMore.set(true);
    this._loading.set(true);

    this.notificationService.getAdminNotifications(this._skip, this._pageSize).subscribe({
      next: (res) => {
        const items = res?.data ?? [];
        this._notifications.set(items);
        this._skip = items.length;
        if (items.length < this._pageSize) this._hasMore.set(false);
        this._loading.set(false);
      },
      error: () => {
        this._loading.set(false);
      },
    });
  }

  // Load the next page of inbox notifications and append to the list.
  loadMore(): void {
    if (!this._hasMore() || this._loading()) return;
    this._loading.set(true);

    this.notificationService.getAdminNotifications(this._skip, this._pageSize).subscribe({
      next: (res) => {
        const items = res?.data ?? [];
        const existing = this._notifications();
        const newItems = items.filter((i) => !existing.some((e) => e.id === i.id));
        if (newItems.length) this._notifications.update((list) => [...list, ...newItems]);
        this._skip += items.length;
        if (items.length < this._pageSize) this._hasMore.set(false);
        this._loading.set(false);
      },
      error: () => {
        this._loading.set(false);
      },
    });
  }

  markOneRead(id: string): void {
    this._notifications.update((list) =>
      list.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
    );
    this.notificationService.markNotificationRead(id).subscribe({ error: () => {} });
  }

  markAllRead(): void {
    const now = new Date().toISOString();
    this._notifications.update((list) =>
      list.map((n) => (n.read_at === null ? { ...n, read_at: now } : n))
    );
    this.notificationService.markAllAsRead().subscribe({
      next: () => this.refresh(this._pageSize),
      error: () => {},
    });
  }

  reset(): void {
    this._notifications.set([]);
    this._skip = 0;
    this._pageSize = 5;
    this._hasMore.set(true);
    this._loading.set(false);
  }
}
